import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  FilmReel, Plus, Sparkle, CircleNotch, Trash, PencilSimple, Check,
  CaretLeft, ArrowRight, UserCirclePlus, Play, ChatCircleDots,
  Lightning, Eye, X, Users, FilmSlate, VideoCamera, Image, DownloadSimple, Target,
} from "@phosphor-icons/react";
import { API, BACKEND_URL, getAuthHeaders } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { downloadFromUrl } from "@/components/shared";
import EpisodeScriptModal from "@/components/EpisodeScriptModal";
import EpisodeProgressPanel from "@/components/EpisodeProgressPanel";
import LookFeelPanel from "@/components/LookFeelPanel";
import StoryHybridPanel from "@/components/StoryHybridPanel";
import { storyHybridPreflight, submitStoryHybrid, mergeStoryConfig, persistStoryPatch } from "@/lib/storyHybrid";

const GENRE_OPTIONS = [
  "Comedy / Funny",
  "Drama / Emotional",
  "Reality / Day-in-the-Life",
  "Motivational / Inspirational",
  "Educational / Informative",
  "Thriller / Suspense",
  "Satire / Parody",
  "Docu-style / Behind the Scenes",
];

const VIBE_PRESETS = [
  "Insecure (Issa Rae) — witty, relatable, slice-of-life",
  "The Real Housewives — dramatic, over-the-top, confessionals",
  "The Office — mockumentary, dry humor, awkward moments",
  "Selling Sunset — luxury, glamour, business drama",
  "Love Is Blind — emotional reveals, twists, relationship drama",
  "Shark Tank — high-stakes pitches, business tension",
  "Emily in Paris — fashionable, lighthearted, aspirational",
  "Atlanta (Donald Glover) — surreal, creative, culture-forward",
];

const SeriesStudio = () => {
  const { user, refreshUser } = useAuth();
  const northStar = user?.north_star || {};

  const [view, setView] = useState("list");
  const [series, setSeries] = useState([]);
  const [activeSeries, setActiveSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState("");
  const [hybridErrors, setHybridErrors] = useState({});
  const [feedback, setFeedback] = useState("");
  const [generatingVideo, setGeneratingVideo] = useState(null);
  const [generatingCinematic, setGeneratingCinematic] = useState(null);
  const [videoMode, setVideoMode] = useState("talking_head"); // "talking_head" | "cinematic"
  // Multi-shot storyboard editor state
  const [storyboardEditingEp, setStoryboardEditingEp] = useState(null);
  const [storyboardShots, setStoryboardShots] = useState([]);
  const [storyboardLoading, setStoryboardLoading] = useState(false);
  const [storyboardNumShots, setStoryboardNumShots] = useState(3);
  // Look & Feel (persisted per-series visual direction) + preset catalog.
  const [lookFeel, setLookFeel] = useState({ visual_preset: "" });
  const [lookFeelPresets, setLookFeelPresets] = useState([]);
  const [lookFeelSaving, setLookFeelSaving] = useState(false);
  const [showLookFeel, setShowLookFeel] = useState(false);
  // Story-led Hybrid config (format + host + featured character).
  const [storyConfig, setStoryConfig] = useState({ story_format: "standard", host: {}, featured_character: {} });
  const storyCfgVersion = useRef(0);
  const [storyConfigSaving, setStoryConfigSaving] = useState(false);
  const [showStory, setShowStory] = useState(false);
  const hybridRecommended = Boolean(
    storyConfig?.host?.twin_image_id && (storyConfig?.featured_character?.name_role || "").trim()
  );
  // Single source of truth for cinematic economics so displayed copy can't
  // drift from the backend (services/cinematic_billing.py: 2 credits/shot,
  // ~$0.60 provider cost per 8s Veo shot).
  const CINEMATIC_CREDITS_PER_SHOT = 2;
  const CINEMATIC_COST_PER_SHOT = 0.6;
  const PREMIUM_SHOTS = 8;
  const PREMIUM_CREDITS = 18;
  const cineSummary = (n) => ({
    shots: n,
    seconds: n * 8,
    credits: n >= PREMIUM_SHOTS ? PREMIUM_CREDITS : n * CINEMATIC_CREDITS_PER_SHOT,
    cost: (n * CINEMATIC_COST_PER_SHOT).toFixed(2),
  });
  const [previewingShotIdx, setPreviewingShotIdx] = useState(null);
  const [showCinematicHelp, setShowCinematicHelp] = useState(false);
  const [draggingShotIdx, setDraggingShotIdx] = useState(null);
  const [dragOverShotIdx, setDragOverShotIdx] = useState(null);
  const [rerollHintForShot, setRerollHintForShot] = useState(null); // shotIdx of the shot whose hint input is open
  const [rerollHint, setRerollHint] = useState("");
  // Talking-photo provider preference applied to every episode generated this session.
  const [talkingPhotoProvider, setTalkingPhotoProvider] = useState("auto");
  const [talkingPhotoResolution, setTalkingPhotoResolution] = useState("480p");

  // Create form
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newEpCount, setNewEpCount] = useState(8);
  const [newGenre, setNewGenre] = useState("");
  const [newVibe, setNewVibe] = useState("");
  const [newAudience, setNewAudience] = useState("");
  const [newEndGoal, setNewEndGoal] = useState("");
  const [newFinalCta, setNewFinalCta] = useState("");
  const [newWhy, setNewWhy] = useState(""); // narrative: why this series exists
  const [wizardStep, setWizardStep] = useState(1); // 1=WHY, 2=END, 3=ARC

  // Script modal state
  const [scriptModalOpen, setScriptModalOpen] = useState(false);
  const [scriptModalEpisode, setScriptModalEpisode] = useState(null);

  // Inline-edit state for episode fields
  const [editingEpField, setEditingEpField] = useState(null); // {epNum, field}
  const [editValue, setEditValue] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [enrichingTakeaways, setEnrichingTakeaways] = useState(false);

  // Character form
  const [showCharForm, setShowCharForm] = useState(false);
  const [charName, setCharName] = useState("");
  const [charDesc, setCharDesc] = useState("");
  const [generatingCharImg, setGeneratingCharImg] = useState(null);

  // Live video-render progress per episode (episode_number → progress dict from
  // /series/{id}/video-progress). Populated by the useEffect below.
  const [videoProgress, setVideoProgress] = useState({});
  const [stepOrder, setStepOrder] = useState([
    "queued", "script", "voice", "talking_photo", "captions", "watermarking", "finalize", "complete",
  ]);
  const [stepLabels, setStepLabels] = useState({});

  const fetchSeries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/series`, { headers: getAuthHeaders() });
      setSeries(res.data.series || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSeries(); }, [fetchSeries]);

  // Reload/terminal-state safety: when a series is loaded, surface a persisted
  // recoverable error and resume polling if a storyboard generation is still
  // running — so a page reload (or a restart-recovered job) never leaves an
  // endless "Writing storyboard…" spinner.
  useEffect(() => {
    if (!activeSeries?.id) return;
    const gs = activeSeries.generation_status;
    if (gs === "failed" && activeSeries.generation_error) {
      setGenError(activeSeries.generation_error);
      setGenerating(false);
      return;
    }
    if (gs !== "generating") return;
    setGenerating(true);
    let stop = false;
    const started = Date.now();
    const poll = setInterval(async () => {
      if (stop) return;
      try {
        const res = await axios.get(`${API}/series/${activeSeries.id}/generation-status`, { headers: getAuthHeaders() });
        const st = res.data.generation_status;
        if (st === "completed") {
          stop = true; clearInterval(poll); setGenerating(false); setGenError("");
          const full = await axios.get(`${API}/series/${activeSeries.id}`, { headers: getAuthHeaders() });
          setActiveSeries(full.data.series); setView("storyboard"); fetchSeries();
        } else if (st === "failed") {
          stop = true; clearInterval(poll); setGenerating(false);
          setGenError(res.data.generation_error || "Storyboard generation failed. Please click Generate to try again.");
        } else if (Date.now() - started > 240000) {
          stop = true; clearInterval(poll); setGenerating(false);
          setGenError("This is taking longer than expected. Please click Generate to try again.");
        }
      } catch (e) { /* transient — keep polling */ }
    }, 4000);
    return () => { stop = true; clearInterval(poll); };
  }, [activeSeries?.id, activeSeries?.generation_status, activeSeries?.generation_error, fetchSeries]);

  // Load the Look & Feel preset catalog once.
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/series/look-feel/presets`, { headers: getAuthHeaders() });
        setLookFeelPresets(res.data?.presets || []);
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  // Load the active series' saved Look & Feel + Story config when it changes.
  useEffect(() => {
    if (!activeSeries?.id) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/series/${activeSeries.id}/look-feel`, { headers: getAuthHeaders() });
        setLookFeel(res.data?.look_feel || { visual_preset: "" });
      } catch (e) { setLookFeel({ visual_preset: "" }); }
      try {
        const sc = await axios.get(`${API}/series/${activeSeries.id}/story-config`, { headers: getAuthHeaders() });
        storyCfgVersion.current++; // authoritative load supersedes any in-flight patch
        setStoryConfig(sc.data || { story_format: "standard", host: {}, featured_character: {} });
      } catch (e) { setStoryConfig({ story_format: "standard", host: {}, featured_character: {} }); }
    })();
  }, [activeSeries?.id]);

  // Make Story Hybrid the default recommended mode when the series already has
  // a saved AI Twin host + a featured character. Talking Head / Cinematic stay
  // available if the user switches.
  useEffect(() => {
    if (storyConfig?.story_format === "story_hybrid" &&
        storyConfig?.host?.twin_image_id &&
        (storyConfig?.featured_character?.name_role || "").trim()) {
      setVideoMode("story_hybrid");
    }
  }, [storyConfig?.story_format, storyConfig?.host?.twin_image_id, storyConfig?.featured_character?.name_role]);

  // Field-level persistence contract: optimistically merge the PATCH locally,
  // then persist ONLY that patch to the server, which atomically merges it and
  // returns the FULL config. A version guard drops out-of-order/stale responses
  // so an older response can never overwrite newer local state.
  const patchStoryConfig = (patch) => {
    setStoryConfig((prev) => mergeStoryConfig(prev, patch));
    if (!activeSeries?.id) return;
    persistStoryPatch({
      axios, API, seriesId: activeSeries.id, headers: getAuthHeaders(),
      patch, versionRef: storyCfgVersion, setStoryConfig,
    });
  };

  const saveStoryConfig = async () => {
    if (!activeSeries?.id) return;
    setStoryConfigSaving(true);
    const v = ++storyCfgVersion.current;
    try {
      const res = await axios.put(`${API}/series/${activeSeries.id}/story-config`, storyConfig, { headers: getAuthHeaders() });
      if (v === storyCfgVersion.current && res.data) setStoryConfig(res.data);
    } catch (e) {
      alert("Could not save Story setup: " + (e.response?.data?.detail || e.message));
    } finally {
      setStoryConfigSaving(false);
    }
  };

  const updateLookFeelField = (key, val) => setLookFeel((p) => ({ ...p, [key]: val }));

  const saveLookFeel = async () => {
    if (!activeSeries?.id) return;
    setLookFeelSaving(true);
    try {
      const res = await axios.put(
        `${API}/series/${activeSeries.id}/look-feel`,
        { look_feel: lookFeel },
        { headers: getAuthHeaders() }
      );
      setLookFeel(res.data?.look_feel || lookFeel);
    } catch (e) {
      alert("Could not save Look & Feel: " + (e.response?.data?.detail || e.message));
    } finally {
      setLookFeelSaving(false);
    }
  };

  // Hydrate videoProgress from the persisted series doc so completed
  // episodes show their preview + download instantly (no need to wait
  // for the first poll tick which only runs while jobs are active).
  useEffect(() => {
    if (!activeSeries?.id) return;
    const eps = activeSeries.episodes || [];
    setVideoProgress((prev) => {
      const next = { ...prev };
      eps.forEach((e) => {
        if (next[e.episode_number]) return; // don't overwrite live poll data
        if (e.output_url && e.job_id) {
          next[e.episode_number] = {
            job_id: e.job_id, status: "completed",
            current_step: "complete", current_step_label: "Complete",
            progress_pct: 100, steps_completed: [], output_url: e.output_url,
          };
        } else if (e.cinematic_video_url && e.cinematic_task_id) {
          next[e.episode_number] = {
            cinematic_task_id: e.cinematic_task_id,
            cinematic_status: "completed",
            cinematic_output_url: e.cinematic_video_url,
          };
        } else if (e.cinematic_task_id && (e.cinematic_status === "failed" || e.cinematic_status === "partial")) {
          // Persisted failed/partial cinematic (e.g., page refreshed after the
          // render failed): mount the panel immediately from stored fields;
          // the one-shot /video-progress fetch below upgrades it with the
          // sanitized per-shot error list.
          next[e.episode_number] = {
            cinematic_task_id: e.cinematic_task_id,
            cinematic_status: e.cinematic_status,
            cinematic_error: e.cinematic_error,
            cinematic_output_url: e.cinematic_video_url,
          };
        } else if (e.job_id && e.status === "failed") {
          next[e.episode_number] = {
            job_id: e.job_id, status: "failed", current_step: "failed",
            error: e.error,
          };
        }
      });
      return next;
    });
    // One-shot progress fetch so failed/partial episodes get their sanitized
    // shot errors even when nothing is actively "generating".
    (async () => {
      try {
        const r = await axios.get(`${API}/series/${activeSeries.id}/video-progress`, { headers: getAuthHeaders() });
        const map = {};
        (r.data.episodes || []).forEach((row) => { map[row.episode_number] = row; });
        setVideoProgress((prev) => {
          const merged = { ...prev };
          Object.keys(map).forEach((k) => {
            const cur = merged[k];
            // Don't clobber a live/among-poll richer state, but always fill in
            // terminal cinematic diagnostics.
            if (!cur || ["failed", "partial", "completed"].includes(map[k].cinematic_status) || ["failed", "completed"].includes(map[k].status)) {
              merged[k] = { ...(cur || {}), ...map[k] };
            }
          });
          return merged;
        });
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSeries?.id]);

  // Live poll video-render progress for every episode of the active series.
  // Runs every 5s as long as at least one episode is "generating" (either
  // talking-head or cinematic). Auto-stops when everything is done or failed.
  useEffect(() => {
    if (!activeSeries?.id) return;
    const eps = activeSeries.episodes || [];
    const activeCount = eps.filter(
      (e) => e.status === "generating" || e.cinematic_status === "generating"
        || (videoProgress[e.episode_number]?.status &&
            !["completed", "failed", "cancelled"].includes(videoProgress[e.episode_number].status)
            && videoProgress[e.episode_number]?.job_id)
    ).length;
    if (activeCount === 0) return;

    let cancelled = false;
    const tick = async () => {
      try {
        const res = await axios.get(
          `${API}/series/${activeSeries.id}/video-progress`,
          { headers: getAuthHeaders() }
        );
        if (cancelled) return;
        const byEp = {};
        (res.data.episodes || []).forEach((r) => { byEp[r.episode_number] = r; });
        setVideoProgress(byEp);
        if (Array.isArray(res.data.step_order)) setStepOrder(res.data.step_order);
        if (res.data.step_labels) setStepLabels(res.data.step_labels);

        // Sync completed / failed states into activeSeries.episodes so the
        // rest of the UI (badges, download buttons) reflects reality.
        setActiveSeries((prev) => {
          if (!prev) return prev;
          const next = { ...prev, episodes: (prev.episodes || []).map((e) => {
            const p = byEp[e.episode_number];
            if (!p) return e;
            const patch = {};
            if (p.status === "completed" && p.output_url) {
              patch.status = "completed";
              patch.output_url = p.output_url;
            } else if (p.status === "failed") {
              patch.status = "failed";
              patch.error = p.error;
            }
            if (p.cinematic_status === "completed" && p.cinematic_output_url) {
              patch.cinematic_status = "completed";
              patch.cinematic_video_url = p.cinematic_output_url;
            }
            return Object.keys(patch).length ? { ...e, ...patch } : e;
          })};
          return next;
        });
      } catch (err) {
        console.warn("progress poll failed", err);
      }
    };
    // Kick immediately then interval.
    tick();
    const t = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSeries?.id, activeSeries?.episodes?.map((e) => `${e.episode_number}:${e.status}:${e.cinematic_status}`).join("|")]);

  const handleSuggest = async () => {
    setSuggesting(true);
    try {
      const res = await axios.post(`${API}/series/suggest`, {}, { headers: getAuthHeaders() });
      setSuggestions(res.data.suggestions || []);
    } catch (e) { alert(e.response?.data?.detail || "Failed to generate suggestions"); }
    finally { setSuggesting(false); }
  };

  const handleCreate = async (title, desc, epCount) => {
    try {
      // Compose full description including the WHY if provided
      const finalDesc = [newWhy.trim(), (desc || newDesc).trim()].filter(Boolean).join("\n\n");
      const res = await axios.post(`${API}/series/create`, {
        title: title || newTitle,
        description: finalDesc || newDesc,
        episode_count: epCount || newEpCount,
        genre: newGenre,
        vibe_reference: newVibe,
        target_audience: newAudience,
        end_goal: newEndGoal || northStar.conversion_metric || "",
        final_cta: newFinalCta || northStar.primary_cta || "",
      }, { headers: getAuthHeaders() });
      setActiveSeries(res.data.series);
      setView("detail");
      fetchSeries();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to create series");
    }
  };

  // Inline update of a single episode field (title, synopsis, hook, cta, takeaway)
  const handleUpdateEpisode = async (episodeNumber, patch) => {
    if (!activeSeries) return;
    setSavingEdit(true);
    try {
      const res = await axios.patch(
        `${API}/series/${activeSeries.id}/episode/${episodeNumber}`,
        patch,
        { headers: getAuthHeaders() }
      );
      setActiveSeries(res.data.series);
      setEditingEpField(null);
      setEditValue("");
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to save");
    } finally {
      setSavingEdit(false);
    }
  };

  const openScriptModalFor = (episode) => {
    setScriptModalEpisode(episode);
    setScriptModalOpen(true);
  };

  const handleEnrichTakeaways = async () => {
    if (!activeSeries) return;
    const missingCount = (activeSeries.episodes || []).filter((ep) => !(ep.takeaway || "").trim()).length;
    if (missingCount === 0) return;
    if (!window.confirm(`Add a Key Takeaway to ${missingCount} episode(s)? This uses ${missingCount} credit(s) and does NOT change your existing storyboard.`)) return;
    setEnrichingTakeaways(true);
    try {
      const res = await axios.post(
        `${API}/series/${activeSeries.id}/enrich-takeaways`,
        {},
        { headers: getAuthHeaders() }
      );
      if (res.data.series) setActiveSeries(res.data.series);
      await refreshUser();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to add takeaways");
    } finally {
      setEnrichingTakeaways(false);
    }
  };

  const handleGenerateEpisodes = async () => {
    if (!activeSeries) return;
    setGenerating(true);
    setGenError("");
    try {
      await axios.post(`${API}/series/${activeSeries.id}/generate-episodes`, {
        feedback,
      }, { headers: getAuthHeaders() });
      setFeedback("");
      let done = false;
      const finish = (series) => {
        done = true;
        clearInterval(poll);
        setActiveSeries(series);
        setView("storyboard");
        setGenerating(false);
        setGenError("");
        fetchSeries();
      };
      // Start polling for completion
      const poll = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/series/${activeSeries.id}/generation-status`, { headers: getAuthHeaders() });
          const status = res.data.generation_status;
          if (status === "completed") {
            const full = await axios.get(`${API}/series/${activeSeries.id}`, { headers: getAuthHeaders() });
            finish(full.data.series);
          } else if (status === "failed") {
            done = true;
            clearInterval(poll);
            setGenerating(false);
            setGenError(res.data.generation_error || "Storyboard generation failed. Please try again.");
          }
        } catch (e) { console.warn("Poll error, retrying:", e); }
      }, 3000);
      // Safety timeout: do ONE final status check rather than silently stopping.
      setTimeout(async () => {
        if (done) return;
        clearInterval(poll);
        try {
          const res = await axios.get(`${API}/series/${activeSeries.id}/generation-status`, { headers: getAuthHeaders() });
          if (res.data.generation_status === "completed") {
            const full = await axios.get(`${API}/series/${activeSeries.id}`, { headers: getAuthHeaders() });
            finish(full.data.series);
            return;
          }
          setGenError(res.data.generation_error || "This is taking longer than expected. Please click Generate to try again.");
        } catch (e) {
          setGenError("This is taking longer than expected. Please click Generate to try again.");
        }
        setGenerating(false);
      }, 240000);
    } catch (e) {
      setGenError(e.response?.data?.detail || "Failed to start storyboard generation.");
      setGenerating(false);
    }
  };

  const handleAddCharacter = async () => {
    if (!charName.trim() || !activeSeries) return;
    try {
      const res = await axios.post(`${API}/series/${activeSeries.id}/characters`, {
        name: charName, description: charDesc,
      }, { headers: getAuthHeaders() });
      setActiveSeries(prev => ({
        ...prev,
        characters: [...(prev.characters || []), res.data.character],
      }));
      setCharName("");
      setCharDesc("");
      setShowCharForm(false);
    } catch (e) { console.error(e); }
  };

  const handleRemoveCharacter = async (charId) => {
    try {
      await axios.delete(`${API}/series/${activeSeries.id}/characters/${charId}`, { headers: getAuthHeaders() });
      setActiveSeries(prev => ({
        ...prev,
        characters: (prev.characters || []).filter(c => c.id !== charId),
      }));
    } catch (e) { console.error(e); }
  };

  const handleGenerateCharImage = async (charId, description) => {
    setGeneratingCharImg(charId);
    try {
      const res = await axios.post(`${API}/series/generate-character-image`, {
        prompt: description,
      }, { headers: getAuthHeaders() });
      // Update character with image info
      if (res.data.image_url) {
        setActiveSeries(prev => ({
          ...prev,
          characters: (prev.characters || []).map(c =>
            c.id === charId ? { ...c, image_url: res.data.image_url } : c
          ),
        }));
      }
    } catch (e) {
      alert(e.response?.data?.detail || "Image generation failed — check your muapi.ai API key.");
    } finally {
      setGeneratingCharImg(null);
    }
  };

  const handleApprove = async () => {
    try {
      await axios.post(`${API}/series/${activeSeries.id}/approve`, {}, { headers: getAuthHeaders() });
      setActiveSeries(prev => ({ ...prev, status: "approved" }));
      fetchSeries();
    } catch (e) { console.error(e); }
  };

  // Story Hybrid render: host AI Twin narrates while the featured character's
  // story unfolds. Reuses the cinematic-task poller. Idempotent/resumable on
  // the backend (successful shots are reused, only failed shots retried).
  const pollHybridTask = (epNum, taskId) => {
    const pollEndpoint = `${API}/series/cinematic-task/${taskId}`;
    const started = Date.now();
    const poll = setInterval(async () => {
      try {
        const { data } = await axios.get(pollEndpoint, { headers: getAuthHeaders() });
        if (data.status === "completed" && data.output_url) {
          clearInterval(poll);
          setActiveSeries((prev) => ({
            ...prev,
            episodes: prev.episodes.map((e) =>
              e.episode_number === epNum ? { ...e, cinematic_status: "completed", cinematic_video_url: data.output_url, cinematic_error: null } : e),
          }));
          setGeneratingCinematic(null);
          fetchSeries();
        } else if (data.status === "failed" || data.status === "partial") {
          clearInterval(poll);
          setActiveSeries((prev) => ({
            ...prev,
            episodes: prev.episodes.map((e) =>
              e.episode_number === epNum ? { ...e, cinematic_status: data.status, cinematic_task_id: taskId, cinematic_error: data.error || (data.status === "partial" ? "Some shots failed — Resume to finish. Your credits are safe." : "Story hybrid render failed. Your credits are safe.") } : e),
          }));
          setGeneratingCinematic(null);
        } else if (Date.now() - started > 600000) {
          clearInterval(poll);
          setGeneratingCinematic(null);
        }
      } catch (e) { console.warn("hybrid poll retry", e); }
    }, 4000);
  };

  const handleGenerateStoryHybrid = async (epNum) => {
    setGenError("");
    setHybridErrors((prev) => ({ ...prev, [epNum]: "" }));
    const ep = activeSeries?.episodes?.find((e) => e.episode_number === epNum);
    // Validate against the SERIES DOC (the exact source the backend reads),
    // overlaid with any unsaved storyConfig edits — so a stale/failed
    // /story-config UI-state load can't cause a false rejection.
    const effectiveConfig = mergeStoryConfig(
      {
        story_format: activeSeries?.story_format,
        host: activeSeries?.host || {},
        featured_character: activeSeries?.featured_character || {},
      },
      storyConfig || {}
    );
    const pre = storyHybridPreflight(effectiveConfig, ep);
    if (!pre.ok) {
      setGenError(pre.message);
      setHybridErrors((prev) => ({ ...prev, [epNum]: pre.message }));
      return;
    }
    setGeneratingCinematic(epNum);
    try {
      const { taskId } = await submitStoryHybrid({
        API, seriesId: activeSeries.id, epNum, headers: getAuthHeaders(),
      });
      setActiveSeries((prev) => ({
        ...prev,
        episodes: prev.episodes.map((e) =>
          e.episode_number === epNum ? { ...e, cinematic_status: "generating", cinematic_task_id: taskId, cinematic_error: null } : e),
      }));
      pollHybridTask(epNum, taskId);
    } catch (e) {
      const msg = e.message || "Story Hybrid render couldn't start.";
      setGenError(msg);
      setHybridErrors((prev) => ({ ...prev, [epNum]: msg }));
      setGeneratingCinematic(null);
    }
  };

  const handleResumeStoryHybrid = async (epNum) => {
    const ep = activeSeries.episodes.find((e) => e.episode_number === epNum);
    const taskId = ep?.cinematic_task_id;
    if (!taskId) return;
    setGeneratingCinematic(epNum);
    try {
      const res = await axios.post(
        `${API}/series/${activeSeries.id}/episodes/${epNum}/resume-story-hybrid`,
        { task_id: taskId }, { headers: getAuthHeaders() });
      const rid = res.data?.task_id || taskId;
      setActiveSeries((prev) => ({
        ...prev,
        episodes: prev.episodes.map((e) =>
          e.episode_number === epNum ? { ...e, cinematic_status: "generating", cinematic_task_id: rid, cinematic_error: null } : e),
      }));
      pollHybridTask(epNum, rid);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Resume failed.";
      setActiveSeries((prev) => ({
        ...prev,
        episodes: prev.episodes.map((x) =>
          x.episode_number === epNum ? { ...x, cinematic_status: "failed", cinematic_error: msg } : x),
      }));
      setGeneratingCinematic(null);
    }
  };



  const handleGenerateCinematicVideo = async (epNum) => {
    setGeneratingCinematic(epNum);
    try {
      // If episode has a saved storyboard with 2+ shots → multi-shot path. Otherwise single-shot fallback.
      const ep = activeSeries.episodes.find((e) => e.episode_number === epNum);
      const hasStoryboard = ep?.storyboard && ep.storyboard.length >= 1;
      const endpoint = hasStoryboard
        ? `${API}/series/${activeSeries.id}/generate-cinematic-multishot/${epNum}`
        : `${API}/series/${activeSeries.id}/generate-cinematic-video/${epNum}`;
      const res = await axios.post(endpoint, {}, { headers: getAuthHeaders() });
      const taskId = res.data?.task_id;
      if (!taskId) throw new Error("No task_id returned from cinematic endpoint");
      const pollEndpoint = `${API}/series/cinematic-task/${taskId}`;
      setActiveSeries((prev) => ({
        ...prev,
        episodes: prev.episodes.map((e) =>
          e.episode_number === epNum ? { ...e, cinematic_status: "generating" } : e
        ),
      }));
      const startedAt = Date.now();
      const MAX_MS = 10 * 60 * 1000; // multi-shot needs longer
      while (Date.now() - startedAt < MAX_MS) {
        await new Promise((r) => setTimeout(r, 5000));
        const poll = await axios.get(pollEndpoint, { headers: getAuthHeaders() });
        const data = poll.data || {};
        if (data.status === "completed") {
          const url = data.result?.video_url;
          setActiveSeries((prev) => ({
            ...prev,
            episodes: prev.episodes.map((e) =>
              e.episode_number === epNum ? { ...e, cinematic_status: "completed", cinematic_video_url: url } : e
            ),
          }));
          return;
        }
        if (data.status === "failed") {
          throw new Error(data.error || "Cinematic generation failed");
        }
      }
      throw new Error("Cinematic generation timed out after 10 minutes.");
    } catch (e) {
      console.error("Cinematic video error:", e);
      // Store the friendly message on the episode so EpisodeProgressPanel
      // can render it inline instead of a jarring alert() dialog with a raw dict.
      const detail = e.response?.data?.detail;
      // FastAPI validation errors return an array/object detail — stringify it
      // so we never end up with "[object Object]" or an empty message.
      const rawMsg = typeof detail === "string"
        ? detail
        : (detail ? (detail?.[0]?.msg || JSON.stringify(detail)) : (e.message || "Something went wrong."));
      // Cleanup: collapse ONLY brace groups that contain a colon (dict-ish
      // dumps) so we strip raw payloads WITHOUT blanking messages that merely
      // contain placeholders like {id}. Then map the known Veo hint.
      let friendly = String(rawMsg)
        .replace(/\{[^{}]*:[^{}]*\}/g, "")
        .replace(/Veo submit returned no task_id.*/i,
          "The video provider is temporarily unavailable. Your credits are safe — please try again in a minute, or open the Lab Assistant chat (bottom-right) if it keeps happening.")
        .trim();
      if (!friendly) friendly = "Cinematic render failed. Your credits are safe — please try again shortly.";
      setActiveSeries((prev) => ({
        ...prev,
        episodes: prev.episodes.map((ep) =>
          ep.episode_number === epNum
            ? { ...ep, cinematic_status: "failed", cinematic_error: friendly }
            : ep
        ),
      }));
    } finally {
      setGeneratingCinematic(null);
    }
  };

  // Open the storyboard editor for an episode. If a storyboard already
  // exists on the episode we load it; otherwise we LLM-generate one.
  const openStoryboard = async (epNum) => {
    setStoryboardEditingEp(epNum);
    const ep = activeSeries.episodes.find((e) => e.episode_number === epNum);
    if (ep?.storyboard && ep.storyboard.length > 0) {
      setStoryboardShots(ep.storyboard);
      setStoryboardNumShots(ep.storyboard.length);
      return;
    }
    setStoryboardLoading(true);
    setStoryboardShots([]);
    try {
      const res = await axios.post(
        `${API}/series/${activeSeries.id}/storyboard/${epNum}`,
        { num_shots: storyboardNumShots },
        { headers: getAuthHeaders() }
      );
      setStoryboardShots(res.data?.shots || []);
      // Hydrate on the episode so the badge/state reflects it
      setActiveSeries((prev) => ({
        ...prev,
        episodes: prev.episodes.map((e) =>
          e.episode_number === epNum ? { ...e, storyboard: res.data?.shots || [] } : e
        ),
      }));
    } catch (e) {
      alert("Storyboard generation failed: " + (e.response?.data?.detail || e.message));
      setStoryboardEditingEp(null);
    } finally {
      setStoryboardLoading(false);
    }
  };

  const regenerateStoryboard = async () => {
    if (!storyboardEditingEp) return;
    setStoryboardLoading(true);
    try {
      const res = await axios.post(
        `${API}/series/${activeSeries.id}/storyboard/${storyboardEditingEp}`,
        { num_shots: storyboardNumShots },
        { headers: getAuthHeaders() }
      );
      setStoryboardShots(res.data?.shots || []);
      setActiveSeries((prev) => ({
        ...prev,
        episodes: prev.episodes.map((e) =>
          e.episode_number === storyboardEditingEp ? { ...e, storyboard: res.data?.shots || [] } : e
        ),
      }));
    } catch (e) {
      alert("Storyboard regen failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setStoryboardLoading(false);
    }
  };

  const saveStoryboard = async () => {
    if (!storyboardEditingEp) return;
    try {
      const res = await axios.patch(
        `${API}/series/${activeSeries.id}/storyboard/${storyboardEditingEp}`,
        { shots: storyboardShots },
        { headers: getAuthHeaders() }
      );
      // Trust ONLY the server-confirmed persisted shots (read-back), never the
      // local request body — a phantom save must not look successful.
      const savedShots = res.data?.shots;
      if (!Array.isArray(savedShots) || savedShots.length === 0) {
        throw new Error("Server did not confirm the storyboard was saved.");
      }
      setActiveSeries((prev) => ({
        ...prev,
        episodes: prev.episodes.map((e) =>
          e.episode_number === storyboardEditingEp ? { ...e, storyboard: savedShots } : e
        ),
      }));
      setStoryboardEditingEp(null);
      // Re-verify against the server so the Render button gates on DB truth.
      try {
        const list = await axios.get(`${API}/series`, { headers: getAuthHeaders() });
        setSeries(list.data.series || []);
        const fresh = (list.data.series || []).find((s) => s.id === activeSeries.id);
        if (fresh) setActiveSeries(fresh);
      } catch {}
    } catch (e) {
      alert("Storyboard save failed: " + (e.response?.data?.detail || e.message));
    }
  };

  // Generate a static image preview of a single shot via Gemini Nano Banana
  // — costs ~$0.001 per preview vs $0.60 for the full Veo render.
  const previewShot = async (shotIdx) => {
    if (!storyboardEditingEp) return;
    const shot = storyboardShots[shotIdx];
    if (!shot?.prompt) {
      alert("Add a prompt to this shot first.");
      return;
    }
    setPreviewingShotIdx(shotIdx);
    try {
      const res = await axios.post(
        `${API}/series/${activeSeries.id}/storyboard/${storyboardEditingEp}/preview/${shotIdx}`,
        { prompt: shot.prompt },
        { headers: getAuthHeaders() }
      );
      // Update the shot with the preview URL
      setStoryboardShots((prev) => prev.map((s, i) =>
        i === shotIdx ? { ...s, preview_url: res.data?.preview_url } : s
      ));
    } catch (e) {
      alert("Preview failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setPreviewingShotIdx(null);
    }
  };

  // Re-roll a single shot's prompt while preserving the others.
  const rerollShot = async (shotIdx, hint = "") => {
    if (!storyboardEditingEp) return;
    setPreviewingShotIdx(shotIdx);
    try {
      const res = await axios.post(
        `${API}/series/${activeSeries.id}/storyboard/${storyboardEditingEp}/regenerate-shot/${shotIdx}`,
        hint ? { hint } : {},
        { headers: getAuthHeaders() }
      );
      const newShot = res.data?.shot;
      if (newShot) {
        setStoryboardShots((prev) => prev.map((s, i) =>
          i === shotIdx ? { ...newShot, shot_number: shotIdx + 1 } : s
        ));
      }
      setRerollHintForShot(null);
      setRerollHint("");
    } catch (e) {
      alert("Re-roll failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setPreviewingShotIdx(null);
    }
  };

  // Drag-and-drop reordering using native HTML5 DnD (no new deps).
  const handleShotDragStart = (idx) => setDraggingShotIdx(idx);
  const handleShotDragOver = (e, idx) => {
    e.preventDefault();
    setDragOverShotIdx(idx);
  };
  const handleShotDrop = (targetIdx) => {
    if (draggingShotIdx === null || draggingShotIdx === targetIdx) {
      setDraggingShotIdx(null);
      setDragOverShotIdx(null);
      return;
    }
    setStoryboardShots((prev) => {
      const next = [...prev];
      const [moved] = next.splice(draggingShotIdx, 1);
      next.splice(targetIdx, 0, moved);
      // Re-number shots so shot_number stays sequential
      return next.map((s, i) => ({ ...s, shot_number: i + 1 }));
    });
    setDraggingShotIdx(null);
    setDragOverShotIdx(null);
  };
  const handleShotDragEnd = () => {
    setDraggingShotIdx(null);
    setDragOverShotIdx(null);
  };

  const handleGenerateVideo = async (epNum) => {
    setGeneratingVideo(epNum);
    try {
      const res = await axios.post(
        `${API}/series/${activeSeries.id}/generate-video/${epNum}`,
        {
          talking_photo_provider: talkingPhotoProvider,
          talking_photo_resolution: talkingPhotoResolution,
        },
        { headers: getAuthHeaders() }
      );
      // Update episode status locally
      setActiveSeries(prev => ({
        ...prev,
        episodes: (prev.episodes || []).map(ep =>
          ep.episode_number === epNum ? { ...ep, status: "generating", job_id: res.data.job_id } : ep
        ),
      }));
    } catch (e) {
      alert(e.response?.data?.detail || "Video generation failed");
    } finally {
      setGeneratingVideo(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this series?")) return;
    try {
      await axios.delete(`${API}/series/${id}`, { headers: getAuthHeaders() });
      if (activeSeries?.id === id) { setActiveSeries(null); setView("list"); }
      fetchSeries();
    } catch (e) { console.error(e); }
  };

  const openSeries = async (id) => {
    try {
      const res = await axios.get(`${API}/series/${id}`, { headers: getAuthHeaders() });
      setActiveSeries(res.data.series);
      setView(res.data.series.episodes?.length > 0 ? "storyboard" : "detail");
    } catch (e) { console.error(e); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <CircleNotch size={28} className="animate-spin text-[#E91E8C]" />
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="series-studio">
      {/* LIST VIEW */}
      {view === "list" && (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
                <FilmReel size={24} weight="duotone" className="text-[#E91E8C]" />
                Series Studio
              </h2>
              <p className="text-zinc-500 text-sm mt-1">Create short-form reality series — AI writes the storyboard, you approve and generate</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCinematicHelp(true)}
                className="text-xs px-3 py-1.5 rounded-lg border border-zinc-200 text-zinc-700 hover:bg-zinc-50 font-semibold flex items-center gap-1.5"
                data-testid="show-series-help-btn"
              >
                💡 How it works
              </button>
              <button onClick={() => { setView("create"); setSuggestions([]); setNewTitle(""); setNewDesc(""); setNewGenre(""); setNewVibe(""); setNewAudience(""); setNewEndGoal(""); setNewFinalCta(""); setNewWhy(""); setWizardStep(1); }} className="btn-primary flex items-center gap-2 text-sm" data-testid="new-series-btn">
                <Plus size={16} weight="bold" /> New Series
              </button>
            </div>
          </div>

          {series.length === 0 ? (
            <div className="text-center py-16">
              <FilmSlate size={48} className="text-zinc-300 mx-auto mb-3" />
              <p className="text-zinc-500 font-medium">No series yet</p>
              <p className="text-zinc-400 text-sm mt-1">Create your first reality series</p>
              <button onClick={() => setView("create")} className="btn-primary mt-4 inline-flex items-center gap-2" data-testid="create-first-series">
                <Plus size={16} /> Create Series
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {series.map(s => (
                <div key={s.id} className="card p-4 flex items-center justify-between hover:bg-zinc-50 transition-all" data-testid={`series-${s.id}`}>
                  <button onClick={() => openSeries(s.id)} className="flex items-center gap-4 flex-1 text-left">
                    <div className="w-12 h-12 rounded-xl bg-[#E91E8C]/10 flex items-center justify-center shrink-0">
                      <FilmReel size={22} weight="duotone" className="text-[#E91E8C]" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-zinc-900 text-sm">{s.title}</p>
                      <p className="text-xs text-zinc-400 truncate">{s.genre ? `${s.genre} • ` : ""}{s.description?.substring(0, 60) || "No description"}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          s.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                          s.status === "storyboard" ? "bg-amber-100 text-amber-700" :
                          "bg-zinc-100 text-zinc-500"
                        }`}>{s.status}</span>
                        <span className="text-[10px] text-zinc-400">{s.episodes?.length || 0}/{s.episode_count} episodes</span>
                      </div>
                    </div>
                  </button>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openSeries(s.id)} className="text-xs text-[#E91E8C] font-medium hover:underline"><Eye size={16} /></button>
                    <button onClick={() => handleDelete(s.id)} className="p-1.5 text-zinc-400 hover:text-red-500" data-testid={`delete-series-${s.id}`}><Trash size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* CREATE VIEW */}
      {view === "create" && (
        <>
          <button onClick={() => setView("list")} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700"><CaretLeft size={16} /> Back</button>

          <div className="card p-0 overflow-hidden">
            {/* Framework Callout */}
            <div className="bg-gradient-to-br from-[#E91E8C] via-[#d4177d] to-[#b01369] text-white px-6 py-5">
              <div className="flex items-center gap-2 mb-1">
                <Sparkle size={16} weight="fill" />
                <p className="text-[10px] font-bold uppercase tracking-widest">The Reverse-Engineered Series Framework</p>
              </div>
              <h3 className="text-lg font-bold">A 3-step wizard to build a series that actually converts.</h3>
              <p className="text-xs text-white/80 mt-1 leading-relaxed">
                Start with your WHY → engineer the END (the payoff) → then design the ARC backwards so every episode compounds toward your conversion.
              </p>
            </div>

            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-2 py-4 border-b border-zinc-100 bg-zinc-50">
              {[
                { num: 1, label: "Why" },
                { num: 2, label: "End" },
                { num: 3, label: "Arc" },
              ].map((s, idx) => (
                <React.Fragment key={s.num}>
                  <button
                    onClick={() => setWizardStep(s.num)}
                    className="flex items-center gap-2"
                    data-testid={`wizard-step-${s.num}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                      wizardStep === s.num ? "bg-[#E91E8C] text-white border-[#E91E8C]" :
                      wizardStep > s.num ? "bg-[#E91E8C]/15 text-[#E91E8C] border-[#E91E8C]/30" :
                      "bg-white text-zinc-400 border-zinc-200"
                    }`}>
                      {wizardStep > s.num ? <Check size={14} weight="bold" /> : s.num}
                    </div>
                    <span className={`text-xs font-semibold ${wizardStep === s.num ? "text-zinc-900" : "text-zinc-400"}`}>{s.label}</span>
                  </button>
                  {idx < 2 && <div className="w-8 h-px bg-zinc-200" />}
                </React.Fragment>
              ))}
            </div>

            <div className="p-6 space-y-5">
              {/* STEP 1 — WHY */}
              {wizardStep === 1 && (
                <div className="space-y-4" data-testid="wizard-step-1-content">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#E91E8C]">Step 1</p>
                    <h4 className="text-xl font-bold text-zinc-900">The Why</h4>
                    <p className="text-sm text-zinc-500 mt-1 leading-relaxed">
                      Why does this series exist? What mission are you on, what problem are you solving, and who are you helping?
                      Every episode will be anchored to this — so don't skip it.
                    </p>
                  </div>

                  <button onClick={handleSuggest} disabled={suggesting} className="btn-secondary w-full flex items-center justify-center gap-2 text-sm" data-testid="suggest-series-btn">
                    {suggesting ? <><CircleNotch size={14} className="animate-spin" /> Generating ideas...</> : <><Sparkle size={14} weight="fill" /> Get AI Series Ideas (uses your Brand KB)</>}
                  </button>

                  {suggestions.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">AI Suggestions</p>
                      {suggestions.map((s, i) => (
                        <button key={s.title || i} onClick={() => { setNewTitle(s.title); setNewDesc(s.description); setNewEpCount(s.episodes || 8); setWizardStep(2); }} className="card p-3 w-full text-left hover:border-[#E91E8C]/30 transition-all" data-testid={`suggestion-${i}`}>
                          <p className="font-semibold text-zinc-900 text-sm">{s.title}</p>
                          <p className="text-xs text-[#E91E8C] mt-0.5">{s.hook}</p>
                          <p className="text-xs text-zinc-500 mt-1">{s.description}</p>
                        </button>
                      ))}
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1.5">Your WHY <span className="text-zinc-300 font-normal normal-case">(the mission, in your voice)</span></label>
                    <textarea
                      value={newWhy}
                      onChange={(e) => setNewWhy(e.target.value)}
                      rows={4}
                      placeholder={"e.g., Most creators can't afford a content team. I built The Branding Bar so they don't need one. This series proves anyone can run a 7-figure content engine solo."}
                      data-testid="wizard-why-input"
                      className="input-field text-sm resize-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1.5">Target Audience</label>
                    <input
                      value={newAudience}
                      onChange={(e) => setNewAudience(e.target.value)}
                      placeholder="e.g., Solo creators 25-40 scaling personal brands"
                      data-testid="audience-input"
                      className="input-field text-sm"
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() => setWizardStep(2)}
                      disabled={!newWhy.trim()}
                      data-testid="wizard-next-1"
                      className="bg-[#E91E8C] text-white rounded-xl px-6 py-2.5 text-sm font-semibold hover:bg-[#d4177d] disabled:opacity-40 flex items-center gap-2"
                    >
                      Next: Design the End <ArrowRight size={14} />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 2 — END */}
              {wizardStep === 2 && (
                <div className="space-y-4" data-testid="wizard-step-2-content">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#E91E8C]">Step 2</p>
                    <h4 className="text-xl font-bold text-zinc-900">The End (Design This First)</h4>
                    <p className="text-sm text-zinc-500 mt-1 leading-relaxed">
                      What's the FINAL episode's conversion moment? The AI engineers every earlier episode backwards from this payoff.
                      Viewers remember endings — make this one sharp.
                    </p>
                  </div>

                  <div className="rounded-xl bg-gradient-to-br from-[#E91E8C]/10 via-pink-50 to-white border-2 border-[#E91E8C]/30 p-5 space-y-4">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#E91E8C] block mb-1.5">End Goal <span className="text-zinc-400 font-normal normal-case">— what should viewers DO after the final episode?</span></label>
                      <input
                        value={newEndGoal}
                        onChange={(e) => setNewEndGoal(e.target.value)}
                        placeholder={northStar.conversion_metric || "e.g., Sign up for free trial, Buy the ebook, Book a call"}
                        data-testid="series-end-goal-input"
                        className="input-field text-sm"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#E91E8C] block mb-1.5">Final-Episode CTA <span className="text-zinc-400 font-normal normal-case">— exact words (verbatim)</span></label>
                      <input
                        value={newFinalCta}
                        onChange={(e) => setNewFinalCta(e.target.value)}
                        placeholder={northStar.primary_cta || "e.g., Comment AI and I'll DM you the free trial link"}
                        data-testid="series-final-cta-input"
                        className="input-field text-sm"
                      />
                      {!newFinalCta && northStar.primary_cta && (
                        <p className="text-[10px] text-zinc-400 mt-1">
                          Leave blank to auto-use your North Star CTA: "{northStar.primary_cta}"
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <button onClick={() => setWizardStep(1)} className="text-sm text-zinc-500 hover:text-zinc-700 flex items-center gap-1" data-testid="wizard-back-2">
                      <CaretLeft size={14} /> Back
                    </button>
                    <button
                      onClick={() => setWizardStep(3)}
                      data-testid="wizard-next-2"
                      className="bg-[#E91E8C] text-white rounded-xl px-6 py-2.5 text-sm font-semibold hover:bg-[#d4177d] flex items-center gap-2"
                    >
                      Next: Design the Arc <ArrowRight size={14} />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 3 — ARC */}
              {wizardStep === 3 && (
                <div className="space-y-4" data-testid="wizard-step-3-content">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#E91E8C]">Step 3</p>
                    <h4 className="text-xl font-bold text-zinc-900">The Arc</h4>
                    <p className="text-sm text-zinc-500 mt-1 leading-relaxed">
                      Title, setup, and episode count. The AI will reverse-engineer the storyboard so every episode lands a takeaway that compounds toward your final CTA.
                    </p>
                  </div>

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1.5">Series Title</label>
                    <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="e.g., The Solo Creator Diaries" className="input-field text-sm" data-testid="series-title-input" />
                  </div>

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1.5">Format / Setup</label>
                    <textarea value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Describe the format: is it confessional style? Day-in-the-life? Scripted? What happens each episode?" className="input-field text-sm min-h-[80px] resize-none" data-testid="series-desc-input" />
                  </div>

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1.5">Genre / Tone</label>
                    <div className="flex flex-wrap gap-2">
                      {GENRE_OPTIONS.map(g => (
                        <button key={g} onClick={() => setNewGenre(newGenre === g ? "" : g)} data-testid={`genre-${g.split("/")[0].trim().toLowerCase()}`}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${newGenre === g ? "border-[#E91E8C] bg-[#E91E8C]/10 text-[#E91E8C]" : "border-zinc-200 text-zinc-500 hover:border-zinc-300"}`}>
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1.5">Vibe Reference <span className="text-zinc-300 font-normal normal-case">(what show should it feel like?)</span></label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {VIBE_PRESETS.map(v => {
                        const label = v.split("—")[0].trim();
                        return (
                          <button key={v} onClick={() => setNewVibe(newVibe === v ? "" : v)} data-testid={`vibe-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${newVibe === v ? "border-[#E91E8C] bg-[#E91E8C]/10 text-[#E91E8C]" : "border-zinc-200 text-zinc-500 hover:border-zinc-300"}`}>
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    <input value={newVibe.includes("—") ? "" : newVibe} onChange={(e) => setNewVibe(e.target.value)} placeholder="Or type your own... e.g. 'Like Issa Rae's Insecure meets Shark Tank'" className="input-field text-sm" data-testid="vibe-custom-input" />
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="text-xs text-zinc-500 font-medium">Episodes:</label>
                    <input type="number" min={3} max={15} value={newEpCount} onChange={(e) => setNewEpCount(Number(e.target.value))} className="input-field text-sm w-20" data-testid="series-ep-count" />
                    <p className="text-[11px] text-zinc-400">Episode {newEpCount} will be your final conversion episode.</p>
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <button onClick={() => setWizardStep(2)} className="text-sm text-zinc-500 hover:text-zinc-700 flex items-center gap-1" data-testid="wizard-back-3">
                      <CaretLeft size={14} /> Back
                    </button>
                    <button onClick={() => handleCreate()} disabled={!newTitle.trim()} className="bg-[#E91E8C] text-white rounded-xl px-6 py-2.5 text-sm font-semibold hover:bg-[#d4177d] disabled:opacity-40 flex items-center gap-2" data-testid="create-series-btn">
                      <Plus size={14} /> Create Series
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* DETAIL VIEW (pre-storyboard) */}
      {view === "detail" && activeSeries && (
        <>
          <button onClick={() => { setView("list"); setActiveSeries(null); }} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700"><CaretLeft size={16} /> Back</button>

          <div className="card p-5 space-y-4">
            <div>
              <h3 className="text-lg font-bold text-zinc-900">{activeSeries.title}</h3>
              <p className="text-sm text-zinc-500 mt-1">{activeSeries.description}</p>
              {(activeSeries.genre || activeSeries.vibe_reference) && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {activeSeries.genre && <span className="text-[10px] px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">{activeSeries.genre}</span>}
                  {activeSeries.vibe_reference && <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full truncate max-w-xs">{activeSeries.vibe_reference.split("—")[0].trim()}</span>}
                  {activeSeries.target_audience && <span className="text-[10px] px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full truncate max-w-xs">{activeSeries.target_audience}</span>}
                </div>
              )}
            </div>

            {/* Characters */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Characters</p>
                <button onClick={() => setShowCharForm(!showCharForm)} className="text-xs text-[#E91E8C] font-medium flex items-center gap-1 hover:underline" data-testid="add-character-btn">
                  <UserCirclePlus size={14} /> Add Character
                </button>
              </div>

              <div className="flex items-center gap-2 px-3 py-2 bg-[#E91E8C]/5 rounded-lg border border-[#E91E8C]/20 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#E91E8C] flex items-center justify-center text-white text-xs font-bold">You</div>
                <div>
                  <p className="text-sm font-medium text-zinc-800">Your AI Twin</p>
                  <p className="text-[10px] text-zinc-500">Main character — uses your AI Twin images</p>
                </div>
              </div>

              {(activeSeries.characters || []).map(c => (
                <div key={c.id} className="flex items-center gap-2 px-3 py-2 bg-zinc-50 rounded-lg border border-zinc-100 mb-1" data-testid={`character-${c.id}`}>
                  {c.image_url ? (
                    <img src={c.image_url} alt={c.name} className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-zinc-600 text-xs font-bold">{c.name.charAt(0).toUpperCase()}</div>
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-zinc-800">{c.name}</p>
                    <p className="text-[10px] text-zinc-500">{c.description || "No description"}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {c.description && !c.image_url && (
                      <button
                        onClick={() => handleGenerateCharImage(c.id, c.description)}
                        disabled={generatingCharImg === c.id}
                        className="p-1 text-zinc-400 hover:text-[#E91E8C] transition-all"
                        title="Generate AI portrait"
                        data-testid={`gen-char-img-${c.id}`}
                      >
                        {generatingCharImg === c.id ? <CircleNotch size={14} className="animate-spin" /> : <Image size={14} />}
                      </button>
                    )}
                    <button onClick={() => handleRemoveCharacter(c.id)} className="p-1 text-zinc-400 hover:text-red-500"><X size={12} /></button>
                  </div>
                </div>
              ))}

              {showCharForm && (
                <div className="mt-2 p-3 bg-zinc-50 rounded-lg border border-zinc-200 space-y-2">
                  <input value={charName} onChange={(e) => setCharName(e.target.value)} placeholder="Character name..." className="input-field text-sm" data-testid="char-name-input" />
                  <input value={charDesc} onChange={(e) => setCharDesc(e.target.value)} placeholder="e.g. 'A confident Black man in his 30s, business partner, always wearing suits'" className="input-field text-sm" data-testid="char-desc-input" />
                  <p className="text-[10px] text-zinc-400">Tip: Be descriptive — after adding, you can generate an AI portrait from this description</p>
                  <div className="flex gap-2">
                    <button onClick={handleAddCharacter} disabled={!charName.trim()} className="px-3 py-1.5 bg-[#E91E8C] text-white text-xs font-semibold rounded-lg hover:bg-[#d4177d] disabled:opacity-50" data-testid="save-character-btn">Add</button>
                    <button onClick={() => { setShowCharForm(false); setCharName(""); setCharDesc(""); }} className="px-3 py-1.5 text-xs text-zinc-500 hover:text-zinc-700">Cancel</button>
                  </div>
                </div>
              )}
            </div>

            <button onClick={handleGenerateEpisodes} disabled={generating} className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-base" data-testid="generate-storyboard-btn">
              {generating ? <><CircleNotch size={18} className="animate-spin" /> Writing storyboard...</> : <><Sparkle size={18} weight="fill" /> Generate Episode Storyboard</>}
            </button>
            {genError && (
              <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="storyboard-gen-error-initial">
                <span className="font-bold">Generation issue:</span> {genError}
              </div>
            )}
          </div>
        </>
      )}

      {/* STORYBOARD VIEW */}
      {view === "storyboard" && activeSeries && (
        <>
          <button onClick={() => { setView("list"); setActiveSeries(null); }} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700"><CaretLeft size={16} /> Back</button>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-zinc-900">{activeSeries.title}</h3>
              <p className="text-xs text-zinc-400">{activeSeries.genre ? `${activeSeries.genre} • ` : ""}{activeSeries.episodes?.length} episodes &middot; {activeSeries.status}</p>
            </div>
            {activeSeries.status !== "approved" && (() => {
              const validEps = (activeSeries.episodes || []).filter((e) => (e.title || "").trim());
              const expected = activeSeries.episode_count || 0;
              const canApprove = validEps.length > 0 && (!expected || validEps.length === expected);
              return (
                <button
                  onClick={handleApprove}
                  disabled={!canApprove}
                  title={canApprove ? "" : `Generate a complete storyboard first (${validEps.length}/${expected} episodes)`}
                  className="btn-primary flex items-center gap-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="approve-series-btn"
                >
                  <Check size={16} weight="bold" /> Approve Storyboard
                  {!canApprove && expected ? ` (${validEps.length}/${expected})` : ""}
                </button>
              );
            })()}
            {activeSeries.status === "approved" && (
              <span className="flex items-center gap-1.5 text-sm text-emerald-600 font-semibold"><Check size={16} weight="bold" /> Approved — Ready to Generate</span>
            )}
          </div>

          {/* Talking Photo provider picker — applied to every episode generated in this session */}
          {activeSeries.status === "approved" && (
            <div className="card p-4 space-y-3 border-[#E91E8C]/20 bg-gradient-to-br from-[#E91E8C]/5 to-transparent" data-testid="series-provider-picker">
              {/* Mode toggle: Talking Head vs Cinematic vs Story Hybrid */}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#E91E8C] mb-1.5">Video Mode</p>
                <div className="grid grid-cols-3 gap-1.5">
                  <button
                    type="button"
                    onClick={() => setVideoMode("talking_head")}
                    data-testid="series-mode-talking-head"
                    className={`p-2 rounded-lg border text-left transition-all ${videoMode === "talking_head" ? "border-[#E91E8C] bg-[#E91E8C]/10" : "border-zinc-200 hover:border-zinc-300"}`}
                  >
                    <p className="text-xs font-bold text-zinc-900">🎤 Talking Head</p>
                    <p className="text-[10px] text-zinc-500">Your AI Twin speaks the script</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setVideoMode("cinematic")}
                    data-testid="series-mode-cinematic"
                    className={`p-2 rounded-lg border text-left transition-all ${videoMode === "cinematic" ? "border-purple-500 bg-purple-50" : "border-zinc-200 hover:border-zinc-300"}`}
                  >
                    <p className="text-xs font-bold text-zinc-900">🎬 Cinematic (Veo)</p>
                    <p className="text-[10px] text-zinc-500">Stitched 8-sec shots · from {cineSummary(3).credits} credits</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setVideoMode("story_hybrid"); patchStoryConfig({ story_format: "story_hybrid" }); }}
                    data-testid="series-mode-story-hybrid"
                    className={`relative p-2 rounded-lg border text-left transition-all ${videoMode === "story_hybrid" ? "border-indigo-500 bg-indigo-50" : "border-zinc-200 hover:border-zinc-300"}`}
                  >
                    {hybridRecommended && (
                      <span className="absolute -top-1.5 -right-1.5 text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-600 text-white" data-testid="hybrid-recommended-badge">RECOMMENDED</span>
                    )}
                    <p className="text-xs font-bold text-zinc-900">✨ Story Hybrid</p>
                    <p className="text-[10px] text-zinc-500">Host + cinematic · Instagram HD · 58–60s · 40 credits</p>
                  </button>
                </div>
              </div>

              {videoMode === "story_hybrid" && (
                <div className="mt-2 space-y-2">
                  <StoryHybridPanel
                    API={API}
                    authHeaders={getAuthHeaders()}
                    storyConfig={{ ...storyConfig, story_format: "story_hybrid" }}
                    presets={lookFeelPresets}
                    lookFeel={lookFeel}
                    onLookFeelChange={updateLookFeelField}
                    onChange={patchStoryConfig}
                    onSave={async () => { await saveStoryConfig(); await saveLookFeel(); }}
                    saving={storyConfigSaving || lookFeelSaving}
                  />
                </div>
              )}

              {videoMode === "talking_head" && (
              <>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#E91E8C]">Talking Head Model</p>
                  <p className="text-[11px] text-zinc-500">Applies to every episode you generate below.</p>
                </div>
                {talkingPhotoProvider === "heygen" && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700" data-testid="series-heygen-badge">💎 Premium · uses credits</span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                <button
                  type="button"
                  onClick={() => setTalkingPhotoProvider("auto")}
                  className={`p-2 rounded-lg border text-left transition-all ${talkingPhotoProvider === "auto" ? "border-emerald-500 bg-emerald-50" : "border-zinc-200 hover:border-zinc-300"}`}
                  data-testid="series-provider-auto"
                  title="Recommended — Pruna for talking-photo, VEED for lip-sync. Best value."
                >
                  <p className="text-[11px] font-bold text-zinc-900 leading-tight">Auto ✨</p>
                  <p className="text-[9px] text-zinc-500 leading-tight mt-0.5">Best-value engine</p>
                </button>
                <button
                  type="button"
                  onClick={() => setTalkingPhotoProvider("heygen")}
                  className={`p-2 rounded-lg border text-left transition-all ${talkingPhotoProvider === "heygen" ? "border-violet-500 bg-violet-50" : "border-zinc-200 hover:border-zinc-300"}`}
                  data-testid="series-provider-heygen"
                  title="HeyGen HD (Avatar IV) — premium quality. Uses extra video credits per episode."
                >
                  <div className="flex items-center gap-1">
                    <p className="text-[11px] font-bold text-zinc-900 leading-tight">💎 HeyGen</p>
                    <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-violet-200 text-violet-800">HD</span>
                  </div>
                  <p className="text-[9px] text-zinc-500 leading-tight mt-0.5">Best quality · 1080p</p>
                </button>
                <button
                  type="button"
                  onClick={() => setTalkingPhotoProvider("veed")}
                  className={`p-2 rounded-lg border text-left transition-all ${talkingPhotoProvider === "veed" ? "border-[#E91E8C] bg-[#E91E8C]/5" : "border-zinc-200 hover:border-zinc-300"}`}
                  data-testid="series-provider-veed"
                  title="VEED Lipsync — cheapest & fastest for lip-sync mode"
                >
                  <p className="text-[11px] font-bold text-zinc-900 leading-tight">🎤 VEED Lipsync</p>
                  <p className="text-[9px] text-zinc-500 leading-tight mt-0.5">~1 min · lip-sync only</p>
                </button>
              </div>
              </>
              )}
              {videoMode === "cinematic" && (
                <div className="text-[11px] text-zinc-600 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="font-bold text-purple-900 mb-1">🎬 Veo 3.1 Cinematic Mode</p>
                  <p>Each episode is stitched from several 8-second Veo 3.1 shots (through your existing muapi key) with your script narrated over the visuals — no talking head. Pick the number of shots in the storyboard step:</p>
                  <ul className="mt-1.5 space-y-0.5 text-purple-800">
                    <li>• <strong>3 shots</strong> ≈ {cineSummary(3).seconds}s · {cineSummary(3).credits} credits · ~${cineSummary(3).cost} provider <span className="text-purple-500">(default)</span></li>
                    <li>• <strong>4 shots</strong> ≈ {cineSummary(4).seconds}s · {cineSummary(4).credits} credits · ~${cineSummary(4).cost} provider</li>
                    <li>• <strong>6 shots</strong> ≈ {cineSummary(6).seconds}s · {cineSummary(6).credits} credits · ~${cineSummary(6).cost} provider</li>
                    <li>• <strong>8 shots — Premium 60s</strong> ≈ {cineSummary(8).seconds}s · <strong>{PREMIUM_CREDITS} credits flat</strong> · ~${cineSummary(8).cost} provider · continuous narration</li>
                  </ul>
                  <p className="mt-1.5 text-purple-700"><strong>Tip:</strong> Cinematic works best for visual scenes (locations, atmospheres, hero shots). For dialogue-heavy episodes, stick with Talking Head. Preview each shot (~$0.001) before rendering.</p>
                </div>
              )}

              {/* Look & Feel — persisted per-series visual direction, editable
                  BEFORE generating any storyboard. */}
              {videoMode === "cinematic" && (
                <div className="mt-3">
                  <button
                    onClick={() => setShowLookFeel((v) => !v)}
                    className="text-xs font-semibold text-purple-700 hover:text-purple-900 flex items-center gap-1"
                    data-testid="toggle-lookfeel-btn"
                  >
                    {showLookFeel ? "▾" : "▸"} {lookFeel?.visual_preset ? `Look & Feel: ${(lookFeelPresets.find((p) => p.id === lookFeel.visual_preset)?.label) || "Custom"}` : "Set the Look & Feel (optional)"}
                  </button>
                  {showLookFeel && (
                    <div className="mt-2">
                      <LookFeelPanel
                        lookFeel={lookFeel}
                        presets={lookFeelPresets}
                        onChange={updateLookFeelField}
                        onSave={saveLookFeel}
                        saving={lookFeelSaving}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Story-led Hybrid: host AI Twin narrates a featured character's
                  story. Optional, does not affect standard cinematic series. */}
              {videoMode === "cinematic" && (
                <div className="mt-2">
                  <button
                    onClick={() => setShowStory((v) => !v)}
                    className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 flex items-center gap-1"
                    data-testid="toggle-story-btn"
                  >
                    {showStory ? "▾" : "▸"} {storyConfig?.story_format === "story_hybrid" ? "Story-led Hybrid: ON (host + featured character)" : "Use Story-led Hybrid (host tells a client's story)"}
                  </button>
                  {showStory && (
                    <div className="mt-2">
                      <StoryHybridPanel
                        API={API}
                        authHeaders={getAuthHeaders()}
                        storyConfig={storyConfig}
                        onChange={patchStoryConfig}
                        onSave={saveStoryConfig}
                        saving={storyConfigSaving}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Arc Visualization — "work backwards" at a glance */}
          {activeSeries.episodes && activeSeries.episodes.length > 0 && (
            <div className="card p-4 space-y-3" data-testid="series-arc-viz">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#E91E8C]">The Arc</p>
                  <p className="text-sm text-zinc-600">
                    Every episode's takeaway compounds toward your final CTA.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {(() => {
                    const missing = (activeSeries.episodes || []).filter((ep) => !(ep.takeaway || "").trim()).length;
                    if (missing === 0) return null;
                    return (
                      <button
                        onClick={handleEnrichTakeaways}
                        disabled={enrichingTakeaways}
                        data-testid="enrich-takeaways-btn"
                        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 transition-all"
                      >
                        {enrichingTakeaways ? (
                          <><CircleNotch size={12} className="animate-spin" /> Adding takeaways…</>
                        ) : (
                          <><Sparkle size={12} weight="fill" /> Add Takeaways ({missing} missing)</>
                        )}
                      </button>
                    );
                  })()}
                  {activeSeries.end_goal && (
                    <div className="text-right hidden md:block">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">End Goal</p>
                      <p className="text-xs font-semibold text-zinc-700 max-w-xs truncate">{activeSeries.end_goal}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-stretch gap-1 overflow-x-auto pb-2">
                {activeSeries.episodes.map((ep, idx) => {
                  const isFinal = idx === activeSeries.episodes.length - 1;
                  return (
                    <React.Fragment key={`arc-${ep.episode_number}`}>
                      <button
                        onClick={() => document.querySelector(`[data-testid="episode-${ep.episode_number}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                        className={`flex-shrink-0 w-40 text-left rounded-lg p-2.5 border transition-all hover:scale-[1.02] ${
                          isFinal
                            ? "bg-gradient-to-br from-[#E91E8C] to-[#d4177d] text-white border-[#E91E8C] shadow-md"
                            : "bg-white border-zinc-200 hover:border-[#E91E8C]/40"
                        }`}
                        data-testid={`arc-ep-${ep.episode_number}`}
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          {isFinal ? <Target size={12} weight="fill" /> : null}
                          <p className={`text-[10px] font-bold uppercase tracking-wider ${isFinal ? "text-white/80" : "text-zinc-400"}`}>
                            Ep {ep.episode_number}{isFinal ? " · Final" : ""}
                          </p>
                        </div>
                        <p className={`text-xs font-semibold line-clamp-2 leading-tight ${isFinal ? "text-white" : "text-zinc-900"}`}>{ep.title || "Untitled"}</p>
                        {ep.takeaway && (
                          <p className={`text-[10px] mt-1.5 line-clamp-2 leading-tight ${isFinal ? "text-white/80" : "text-zinc-500"}`}>
                            💡 {ep.takeaway}
                          </p>
                        )}
                      </button>
                      {idx < activeSeries.episodes.length - 1 && (
                        <div className="flex-shrink-0 flex items-center">
                          <ArrowRight size={16} className="text-zinc-300" />
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-4">
            {(activeSeries.episodes || []).map((ep, idx) => {
              const isFinal = idx === (activeSeries.episodes.length - 1);
              return (
              <div key={ep.episode_number} className={`card p-5 space-y-3 ${isFinal ? "border-2 border-[#E91E8C] bg-gradient-to-br from-[#E91E8C]/5 to-transparent" : ""}`} data-testid={`episode-${ep.episode_number}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold ${isFinal ? "bg-[#E91E8C] text-white" : "bg-zinc-100 text-zinc-600"}`}>
                      {isFinal ? <Target size={16} weight="fill" /> : ep.episode_number}
                    </div>
                    <div>
                      {editingEpField?.epNum === ep.episode_number && editingEpField?.field === "title" ? (
                        <div className="flex items-center gap-1">
                          <input value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus className="input-field text-sm py-1" data-testid={`edit-title-input-${ep.episode_number}`} />
                          <button onClick={() => handleUpdateEpisode(ep.episode_number, { title: editValue })} disabled={savingEdit} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" data-testid={`save-title-${ep.episode_number}`}>
                            {savingEdit ? <CircleNotch size={14} className="animate-spin" /> : <Check size={14} weight="bold" />}
                          </button>
                          <button onClick={() => { setEditingEpField(null); setEditValue(""); }} className="p-1.5 text-zinc-400 hover:bg-zinc-100 rounded"><X size={14} /></button>
                        </div>
                      ) : (
                        <button onClick={() => { setEditingEpField({ epNum: ep.episode_number, field: "title" }); setEditValue(ep.title || ""); }} className="group flex items-center gap-1.5 font-semibold text-zinc-900 text-sm hover:text-[#E91E8C]" data-testid={`edit-title-${ep.episode_number}`}>
                          {ep.title || "(untitled)"}
                          <PencilSimple size={12} className="opacity-0 group-hover:opacity-100 text-zinc-400" />
                        </button>
                      )}
                      {isFinal && <p className="text-[10px] font-bold uppercase tracking-widest text-[#E91E8C]">🎯 Conversion Episode</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openScriptModalFor(ep)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-zinc-900 text-white hover:bg-zinc-700 transition-all"
                      data-testid={`write-script-ep-${ep.episode_number}`}
                    >
                      <Sparkle size={12} weight="fill" /> Write Full Script
                    </button>
                    {activeSeries.status === "approved" && (
                      <>
                        <button
                          onClick={() => handleGenerateVideo(ep.episode_number)}
                          disabled={generatingVideo === ep.episode_number || generatingCinematic === ep.episode_number || ep.status === "generating" || videoMode === "cinematic"}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            videoMode === "cinematic" ? "bg-zinc-100 text-zinc-400" :
                            ep.status === "generating" ? "bg-amber-100 text-amber-700" :
                            ep.job_id ? "bg-emerald-100 text-emerald-700" :
                            "bg-[#E91E8C] text-white hover:bg-[#d4177d]"
                          }`}
                          data-testid={`generate-video-ep-${ep.episode_number}`}
                          title={videoMode === "cinematic" ? "Switch back to Talking Head mode to use this" : ""}
                        >
                          {generatingVideo === ep.episode_number ? <CircleNotch size={12} className="animate-spin" /> :
                           ep.status === "generating" ? <CircleNotch size={12} className="animate-spin" /> :
                           ep.job_id ? <><Check size={12} /> Video Started</> :
                           <><VideoCamera size={12} /> Talking Head</>}
                        </button>
                        <button
                          onClick={() => handleGenerateCinematicVideo(ep.episode_number)}
                          disabled={generatingCinematic === ep.episode_number || generatingVideo === ep.episode_number || ep.cinematic_status === "generating" || videoMode === "talking_head"}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            videoMode === "talking_head" ? "bg-zinc-100 text-zinc-400" :
                            ep.cinematic_status === "generating" ? "bg-amber-100 text-amber-700" :
                            ep.cinematic_video_url ? "bg-emerald-100 text-emerald-700" :
                            "bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white hover:opacity-90"
                          }`}
                          data-testid={`generate-cinematic-ep-${ep.episode_number}`}
                          title={videoMode === "talking_head" ? "Switch to Cinematic mode to use this" : (ep.storyboard?.length > 1 ? `Multi-shot · ${ep.storyboard.length} clips · ~$${(ep.storyboard.length * 0.6).toFixed(2)}` : "Single 8-sec cinematic clip · $0.60 · click Storyboard to break into multi-shot")}
                        >
                          {generatingCinematic === ep.episode_number || ep.cinematic_status === "generating" ?
                            <CircleNotch size={12} className="animate-spin" /> :
                           ep.cinematic_video_url ? <><Check size={12} /> Cinematic Done</> :
                           ep.storyboard?.length > 1 ? <>🎬 Render {ep.storyboard.length}-shot</> :
                           <>🎬 Cinematic</>}
                        </button>
                        {videoMode === "story_hybrid" && (
                          <button
                            onClick={() => handleGenerateStoryHybrid(ep.episode_number)}
                            disabled={generatingCinematic === ep.episode_number || ep.cinematic_status === "generating"}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                              ep.cinematic_status === "generating" ? "bg-amber-100 text-amber-700" :
                              ep.cinematic_video_url ? "bg-emerald-100 text-emerald-700" :
                              "bg-gradient-to-r from-indigo-500 to-violet-500 text-white hover:opacity-90"
                            }`}
                            data-testid={`generate-story-hybrid-ep-${ep.episode_number}`}
                            title="Host + cinematic story · Instagram HD 1080×1920 · 58–60s · 40 credits"
                          >
                            {generatingCinematic === ep.episode_number || ep.cinematic_status === "generating" ?
                              <CircleNotch size={12} className="animate-spin" /> :
                             ep.cinematic_video_url ? <><Check size={12} /> Hybrid Done</> :
                             <>✨ Render Story Hybrid</>}
                          </button>
                        )}
                        {videoMode === "story_hybrid" && ep.cinematic_task_id &&
                         (ep.cinematic_status === "partial" || ep.cinematic_status === "failed") &&
                         generatingCinematic !== ep.episode_number && (
                          <button
                            onClick={() => handleResumeStoryHybrid(ep.episode_number)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-500 text-white hover:bg-amber-600 transition-all"
                            data-testid={`resume-story-hybrid-ep-${ep.episode_number}`}
                            title="Re-render only the failed shots — you won't be charged twice"
                          >
                            🔁 Resume Failed Render
                          </button>
                        )}
                        {videoMode === "story_hybrid" && hybridErrors[ep.episode_number] && (
                          <p className="w-full text-[11px] text-rose-600 font-medium mt-1" data-testid={`story-hybrid-error-ep-${ep.episode_number}`}>
                            {hybridErrors[ep.episode_number]}
                          </p>
                        )}
                        <button
                          onClick={() => openStoryboard(ep.episode_number)}
                          disabled={videoMode === "talking_head"}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                            videoMode === "talking_head" ? "bg-zinc-100 text-zinc-400 border-zinc-200" :
                            ep.storyboard?.length > 0 ? "bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100" :
                            "bg-white text-purple-700 border-purple-300 hover:bg-purple-50"
                          }`}
                          data-testid={`open-storyboard-ep-${ep.episode_number}`}
                          title="Break the episode into multiple visual shots — edit each prompt before paying for generation"
                        >
                          📋 Storyboard{ep.storyboard?.length ? ` (${ep.storyboard.length})` : ""}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Live video-render progress panel */}
                {(videoProgress[ep.episode_number]?.job_id ||
                  videoProgress[ep.episode_number]?.cinematic_task_id ||
                  ep.status === "generating" || ep.status === "failed" ||
                  ep.cinematic_status === "generating" ||
                  ep.cinematic_status === "failed" || ep.cinematic_status === "partial") && (
                  <EpisodeProgressPanel
                    progress={videoProgress[ep.episode_number] || {
                      cinematic_task_id: ep.cinematic_task_id,
                      cinematic_status: ep.cinematic_status,
                      cinematic_error: ep.cinematic_error,
                      job_id: ep.job_id, status: ep.status,
                    }}
                    episode={ep}
                    stepOrder={stepOrder}
                    stepLabels={stepLabels}
                    onRetry={(n) => (ep.cinematic_task_id ? handleGenerateCinematicVideo(n) : handleGenerateVideo(n))}
                    onRegenerate={(n) => (videoMode === "story_hybrid" ? handleGenerateStoryHybrid(n) : handleGenerateCinematicVideo(n))}
                  />
                )}

                {/* Synopsis (inline editable) */}
                {editingEpField?.epNum === ep.episode_number && editingEpField?.field === "synopsis" ? (
                  <div className="space-y-2">
                    <textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} rows={3} className="input-field text-sm resize-none" autoFocus data-testid={`edit-synopsis-input-${ep.episode_number}`} />
                    <div className="flex gap-2">
                      <button onClick={() => handleUpdateEpisode(ep.episode_number, { synopsis: editValue })} disabled={savingEdit} className="px-3 py-1 text-xs bg-emerald-500 text-white rounded-lg" data-testid={`save-synopsis-${ep.episode_number}`}>Save</button>
                      <button onClick={() => { setEditingEpField(null); setEditValue(""); }} className="px-3 py-1 text-xs text-zinc-500">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => { setEditingEpField({ epNum: ep.episode_number, field: "synopsis" }); setEditValue(ep.synopsis || ""); }} className="group text-left w-full" data-testid={`edit-synopsis-${ep.episode_number}`}>
                    <p className="text-sm text-zinc-600 group-hover:text-zinc-900 flex items-start gap-1.5">
                      <span className="flex-1">{ep.synopsis || <span className="italic text-zinc-400">(click to add synopsis)</span>}</span>
                      <PencilSimple size={12} className="mt-0.5 opacity-0 group-hover:opacity-100 text-zinc-400 flex-shrink-0" />
                    </p>
                  </button>
                )}

                {/* TAKEAWAY — the one insight */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5" data-testid={`takeaway-${ep.episode_number}`}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 mb-1 flex items-center gap-1">
                    💡 Key Takeaway <span className="text-emerald-400 font-normal normal-case lowercase">— the ONE thing viewers remember</span>
                  </p>
                  {editingEpField?.epNum === ep.episode_number && editingEpField?.field === "takeaway" ? (
                    <div className="space-y-2">
                      <textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} rows={2} className="input-field text-sm resize-none" autoFocus data-testid={`edit-takeaway-input-${ep.episode_number}`} />
                      <div className="flex gap-2">
                        <button onClick={() => handleUpdateEpisode(ep.episode_number, { takeaway: editValue })} disabled={savingEdit} className="px-3 py-1 text-xs bg-emerald-500 text-white rounded-lg" data-testid={`save-takeaway-${ep.episode_number}`}>Save</button>
                        <button onClick={() => { setEditingEpField(null); setEditValue(""); }} className="px-3 py-1 text-xs text-zinc-500">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => { setEditingEpField({ epNum: ep.episode_number, field: "takeaway" }); setEditValue(ep.takeaway || ""); }} className="group text-left w-full" data-testid={`edit-takeaway-${ep.episode_number}`}>
                      <p className="text-sm text-emerald-900 group-hover:text-emerald-950 flex items-start gap-1.5">
                        <span className="flex-1">{ep.takeaway || <span className="italic text-emerald-500">(click to add a takeaway — what's the ONE insight?)</span>}</span>
                        <PencilSimple size={12} className="mt-0.5 opacity-0 group-hover:opacity-100 text-emerald-500 flex-shrink-0" />
                      </p>
                    </button>
                  )}
                </div>

                {/* Hook */}
                {ep.hook && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <p className="text-[10px] font-bold uppercase text-amber-600 mb-0.5">Opening Hook</p>
                    {editingEpField?.epNum === ep.episode_number && editingEpField?.field === "hook" ? (
                      <div className="space-y-2">
                        <textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} rows={2} className="input-field text-sm resize-none" autoFocus data-testid={`edit-hook-input-${ep.episode_number}`} />
                        <div className="flex gap-2">
                          <button onClick={() => handleUpdateEpisode(ep.episode_number, { hook: editValue })} disabled={savingEdit} className="px-3 py-1 text-xs bg-amber-500 text-white rounded-lg" data-testid={`save-hook-${ep.episode_number}`}>Save</button>
                          <button onClick={() => { setEditingEpField(null); setEditValue(""); }} className="px-3 py-1 text-xs text-zinc-500">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => { setEditingEpField({ epNum: ep.episode_number, field: "hook" }); setEditValue(ep.hook || ""); }} className="group text-left w-full" data-testid={`edit-hook-${ep.episode_number}`}>
                        <p className="text-xs text-amber-800 flex items-start gap-1.5">
                          <span className="flex-1">{ep.hook}</span>
                          <PencilSimple size={10} className="mt-0.5 opacity-0 group-hover:opacity-100 text-amber-500 flex-shrink-0" />
                        </p>
                      </button>
                    )}
                  </div>
                )}

                {/* Scenes */}
                {ep.scenes?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Scenes</p>
                    <div className="space-y-1">
                      {ep.scenes.map((scene, j) => (
                        <div key={`scene-${j}-${scene.slice(0,20)}`} className="flex items-start gap-2 text-xs text-zinc-600">
                          <span className="w-5 h-5 rounded bg-zinc-100 flex items-center justify-center text-[10px] font-bold text-zinc-400 shrink-0">{j + 1}</span>
                          <p>{scene}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* CTA — distinct for final vs soft */}
                <div className={`rounded-lg px-3 py-2.5 border ${isFinal ? "bg-[#E91E8C]/10 border-[#E91E8C]" : "bg-blue-50 border-blue-200"}`}>
                  <p className={`text-[10px] font-bold uppercase mb-1 flex items-center gap-1 ${isFinal ? "text-[#E91E8C]" : "text-blue-600"}`}>
                    {isFinal ? <><Target size={11} weight="fill" /> Final Conversion CTA <span className="font-normal normal-case lowercase text-[#E91E8C]/80">— use verbatim</span></> : <>Soft Stepping-Stone CTA <span className="font-normal normal-case lowercase text-blue-500">— keep them watching</span></>}
                  </p>
                  {editingEpField?.epNum === ep.episode_number && editingEpField?.field === "cta" ? (
                    <div className="space-y-2">
                      <input value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus className="input-field text-sm" data-testid={`edit-cta-input-${ep.episode_number}`} />
                      <div className="flex gap-2">
                        <button onClick={() => handleUpdateEpisode(ep.episode_number, { cta: editValue })} disabled={savingEdit} className={`px-3 py-1 text-xs text-white rounded-lg ${isFinal ? "bg-[#E91E8C]" : "bg-blue-500"}`} data-testid={`save-cta-${ep.episode_number}`}>Save</button>
                        <button onClick={() => { setEditingEpField(null); setEditValue(""); }} className="px-3 py-1 text-xs text-zinc-500">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => { setEditingEpField({ epNum: ep.episode_number, field: "cta" }); setEditValue(ep.cta || ""); }} className="group text-left w-full" data-testid={`edit-cta-${ep.episode_number}`}>
                      <p className={`text-xs flex items-start gap-1.5 ${isFinal ? "text-zinc-900 font-medium" : "text-zinc-700"}`}>
                        <span className="flex-1">{ep.cta || <span className="italic text-zinc-400">(click to add CTA)</span>}</span>
                        <PencilSimple size={10} className={`mt-0.5 opacity-0 group-hover:opacity-100 flex-shrink-0 ${isFinal ? "text-[#E91E8C]" : "text-blue-500"}`} />
                      </p>
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-4 pt-1">
                  <button
                    onClick={() => {
                      const text = [
                        `Episode ${ep.episode_number}: ${ep.title}`,
                        "",
                        ep.takeaway ? `KEY TAKEAWAY: ${ep.takeaway}` : "",
                        "",
                        ep.hook ? `HOOK: ${ep.hook}` : "",
                        "",
                        `SYNOPSIS: ${ep.synopsis}`,
                        "",
                        ep.scenes?.length ? `SCENES:\n${ep.scenes.map((s, i) => `${i + 1}. ${s}`).join("\n")}` : "",
                        "",
                        ep.cta ? `CTA: ${ep.cta}` : "",
                      ].filter(Boolean).join("\n");
                      const blob = new Blob([text], { type: "text/plain" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `episode-${ep.episode_number}-${ep.title?.replace(/\s+/g, "-").slice(0, 30) || "script"}.txt`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                    className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-[#E91E8C] transition-all"
                    data-testid={`download-ep-${ep.episode_number}`}
                  >
                    <DownloadSimple size={14} />
                    Download Outline
                  </button>
                </div>
              </div>
              );
            })}
          </div>

          {/* Feedback & Regenerate */}
          {activeSeries.status !== "approved" && (
            <div className="card p-5 space-y-3">
              {genError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 flex items-start gap-2" data-testid="storyboard-gen-error">
                  <span className="font-bold">Generation issue:</span> <span>{genError}</span>
                </div>
              )}
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                <ChatCircleDots size={14} className="text-[#E91E8C]" /> Give Feedback & Regenerate
              </p>
              <textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="e.g. 'Make episode 3 more dramatic', 'Add a plot twist', 'The tone should be funnier'..." className="input-field text-sm min-h-[80px] resize-none" data-testid="feedback-input" />
              <button onClick={handleGenerateEpisodes} disabled={generating} className="btn-secondary w-full flex items-center justify-center gap-2" data-testid="regenerate-btn">
                {generating ? <><CircleNotch size={16} className="animate-spin" /> Regenerating...</> : <><Lightning size={16} weight="fill" /> {feedback.trim() ? "Regenerate with Feedback" : "Regenerate Storyboard"}</>}
              </button>
            </div>
          )}
        </>
      )}

      {/* Multi-shot cinematic storyboard editor */}
      {storyboardEditingEp !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setStoryboardEditingEp(null)}
          data-testid="storyboard-modal-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-zinc-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-lg font-extrabold text-zinc-900">🎬 Episode {storyboardEditingEp} Storyboard</h3>
                <p className="text-xs text-zinc-500 mt-0.5">Edit each shot's prompt before generating. Each 8-sec shot = ~$0.60 via muapi.</p>
              </div>
              <button
                onClick={() => setStoryboardEditingEp(null)}
                className="text-zinc-400 hover:text-zinc-700 text-2xl leading-none"
                data-testid="close-storyboard-modal"
              >×</button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-xs font-semibold text-zinc-700">Episode length:</label>
                <select
                  value={storyboardNumShots}
                  onChange={(e) => setStoryboardNumShots(parseInt(e.target.value, 10))}
                  className="text-xs border border-zinc-200 rounded px-2 py-1"
                  data-testid="storyboard-num-shots"
                >
                  {[3, 4, 6].map((n) => (
                    <option key={n} value={n}>
                      {n} shots · ~{n * 8}s · {n * 2} credits · ~${(n * 0.6).toFixed(2)} provider
                    </option>
                  ))}
                  <option value={8}>
                    8 shots · Premium ~64s · {PREMIUM_CREDITS} credits flat · ~${(8 * 0.6).toFixed(2)} provider
                  </option>
                </select>
                <span className="text-[11px] text-zinc-500" data-testid="storyboard-cost-hint">
                  {storyboardNumShots * 8}s episode · {cineSummary(storyboardNumShots).credits} credits{storyboardNumShots >= PREMIUM_SHOTS ? " (Premium — continuous narration)" : ""}
                </span>
                <button
                  onClick={regenerateStoryboard}
                  disabled={storyboardLoading}
                  className="text-xs px-3 py-1 rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
                  data-testid="regenerate-storyboard-btn"
                >
                  {storyboardLoading ? "Generating..." : "🪄 AI-regen all shots"}
                </button>
              </div>

              {/* Editable Look & Feel — same source of truth as the pre-gen
                  panel; regenerating shots uses the saved look. */}
              <LookFeelPanel
                lookFeel={lookFeel}
                presets={lookFeelPresets}
                onChange={updateLookFeelField}
                onSave={saveLookFeel}
                saving={lookFeelSaving}
                compact
              />

              {storyboardLoading ? (
                <div className="text-center py-12 text-zinc-500">
                  <CircleNotch size={28} className="animate-spin mx-auto mb-3 text-purple-500" />
                  <p className="text-sm">Building your storyboard...</p>
                </div>
              ) : storyboardShots.length === 0 ? (
                <p className="text-sm text-zinc-500">No shots yet.</p>
              ) : (
                <>
                <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 space-y-1">
                  <p className="font-bold">💡 How this works</p>
                  <p>Each shot below becomes one 8-second cinematic clip. <strong>Drag the ⋮⋮ handle</strong> to reorder shots. Click <strong>🪄 Preview</strong> on any shot to see a still image (~$0.001) — if you like it, the final Veo video will match closely. Edit the prompt and re-preview as many times as you want. When all shots look good, hit <strong>Save Storyboard</strong> and then <strong>🎬 Render</strong> in the storyboard view. Your voice narrates the full script continuously across the shots (no silent gaps at the cuts).</p>
                </div>
                <div className="space-y-3">
                  {storyboardShots.map((shot, idx) => (
                    <div
                      key={idx}
                      draggable
                      onDragStart={() => handleShotDragStart(idx)}
                      onDragOver={(e) => handleShotDragOver(e, idx)}
                      onDrop={() => handleShotDrop(idx)}
                      onDragEnd={handleShotDragEnd}
                      className={`border rounded-lg p-3 space-y-2 transition-all cursor-move ${
                        draggingShotIdx === idx ? "opacity-40 border-purple-400" :
                        dragOverShotIdx === idx && draggingShotIdx !== null ? "border-purple-500 ring-2 ring-purple-300 bg-purple-50" :
                        "border-zinc-200"
                      }`}
                      data-testid={`storyboard-shot-${idx + 1}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className="cursor-grab active:cursor-grabbing text-zinc-400 hover:text-purple-500 select-none"
                          title="Drag to reorder"
                          data-testid={`drag-handle-${idx + 1}`}
                        >⋮⋮</span>
                        <p className="text-xs font-bold text-zinc-900 whitespace-nowrap">Shot {idx + 1} · {shot.duration || 8}s</p>
                        <input
                          type="text"
                          value={shot.description || ""}
                          onChange={(e) => {
                            const next = [...storyboardShots];
                            next[idx] = { ...next[idx], description: e.target.value };
                            setStoryboardShots(next);
                          }}
                          placeholder="One-line shot description"
                          className="flex-1 text-xs border border-zinc-200 rounded px-2 py-1"
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                        <button
                          onClick={() => {
                            if (rerollHintForShot === idx) {
                              rerollShot(idx, rerollHint);
                            } else {
                              setRerollHintForShot(idx);
                              setRerollHint("");
                            }
                          }}
                          disabled={previewingShotIdx !== null}
                          className="whitespace-nowrap text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-50 font-semibold"
                          data-testid={`reroll-shot-${idx + 1}`}
                          title="Re-roll just this shot — keeps the others intact"
                        >
                          🎲 Re-roll
                        </button>
                        <button
                          onClick={() => previewShot(idx)}
                          disabled={previewingShotIdx === idx || !shot.prompt}
                          className="whitespace-nowrap text-xs px-2.5 py-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 disabled:opacity-50 font-semibold"
                          data-testid={`preview-shot-${idx + 1}`}
                          title="Generate a still image preview (~$0.001)"
                        >
                          {previewingShotIdx === idx ? <><CircleNotch size={10} className="animate-spin inline" /> ...</> : shot.preview_url ? "🔄 Re-preview" : "🪄 Preview"}
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <textarea
                          value={shot.prompt || ""}
                          onChange={(e) => {
                            const next = [...storyboardShots];
                            next[idx] = { ...next[idx], prompt: e.target.value };
                            setStoryboardShots(next);
                          }}
                          rows={5}
                          draggable={false}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="flex-1 text-xs border border-zinc-200 rounded px-2 py-1.5 font-mono"
                          placeholder="Full Veo prompt..."
                          data-testid={`storyboard-prompt-${idx + 1}`}
                        />
                        {shot.preview_url && (
                          <a
                            href={`${BACKEND_URL}${shot.preview_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0 w-24 h-32 rounded border border-zinc-200 overflow-hidden bg-zinc-100 hover:ring-2 hover:ring-purple-300 transition-all"
                            data-testid={`preview-img-${idx + 1}`}
                            title="Click to view full size"
                          >
                            <img src={`${BACKEND_URL}${shot.preview_url}`} alt={`Shot ${idx + 1} preview`} className="w-full h-full object-cover" />
                          </a>
                        )}
                      </div>
                      {rerollHintForShot === idx && (
                        <div className="flex items-center gap-2 p-2 rounded bg-amber-50 border border-amber-200" data-testid={`reroll-hint-${idx + 1}`}>
                          <input
                            type="text"
                            value={rerollHint}
                            onChange={(e) => setRerollHint(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") rerollShot(idx, rerollHint); }}
                            placeholder="Optional: tell the AI what to change (e.g. 'more dramatic', 'outdoor', 'closer shot')..."
                            className="flex-1 text-xs border border-amber-300 rounded px-2 py-1 bg-white"
                            autoFocus
                            draggable={false}
                            onMouseDown={(e) => e.stopPropagation()}
                          />
                          <button
                            onClick={() => rerollShot(idx, rerollHint)}
                            className="text-xs px-2.5 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 font-semibold"
                          >Re-roll</button>
                          <button
                            onClick={() => { setRerollHintForShot(null); setRerollHint(""); }}
                            className="text-xs px-2 py-1 text-amber-700 hover:bg-amber-100 rounded"
                          >Cancel</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                </>
              )}
            </div>

            <div className="p-5 border-t border-zinc-200 flex items-center justify-between sticky bottom-0 bg-white">
              <div className="text-xs text-zinc-500">
                Total: <strong>{storyboardShots.length} shots · {storyboardShots.reduce((a, s) => a + (s.duration || 8), 0)}s</strong> · ~<strong>${(storyboardShots.length * 0.6).toFixed(2)}</strong>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setStoryboardEditingEp(null)}
                  className="text-sm px-4 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-100"
                >Cancel</button>
                <button
                  onClick={saveStoryboard}
                  disabled={storyboardShots.length === 0 || storyboardLoading}
                  className="text-sm px-4 py-1.5 rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 font-semibold"
                  data-testid="save-storyboard-btn"
                >Save Storyboard</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* "How it works" instructional modal */}
      {showCinematicHelp && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowCinematicHelp(false)}
          data-testid="series-help-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-zinc-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-extrabold text-zinc-900">💡 How to create a series</h3>
              <button
                onClick={() => setShowCinematicHelp(false)}
                className="text-zinc-400 hover:text-zinc-700 text-2xl leading-none"
                data-testid="close-series-help"
              >×</button>
            </div>
            <div className="p-5 space-y-5 text-sm text-zinc-700">
              <div>
                <p className="font-bold text-zinc-900 mb-1">1. Create a series concept</p>
                <p>Click <strong>+ New Series</strong>. Give it a title, who it's for, the vibe, and what you want viewers to do at the end (the CTA). The clearer your end goal, the better the AI hooks each episode toward conversion.</p>
              </div>
              <div>
                <p className="font-bold text-zinc-900 mb-1">2. Review &amp; approve the storyboard</p>
                <p>The AI proposes 5-7 episode arcs working backwards from your end goal. Click <strong>Approve</strong> when the arc feels right. Don't love it? Hit <strong>Regenerate with Feedback</strong> and tell it what to change ("make episode 3 more dramatic", "shift the tone funnier", etc.).</p>
              </div>
              <div>
                <p className="font-bold text-zinc-900 mb-1">3. Pick your video mode</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li><strong>🎤 Talking Head</strong> — Your AI Twin (cloned face + voice) speaks the script. Best for educational / personality-driven content. ~1-2 min render via Fabric.</li>
                  <li><strong>🎬 Cinematic (Veo)</strong> — No talking head; pure visual storytelling with your voice narrating over cinematic shots. Best for hook/teaser/atmosphere episodes.</li>
                </ul>
              </div>
              <div>
                <p className="font-bold text-zinc-900 mb-1">4. (Cinematic only) Edit the storyboard</p>
                <p>Click <strong>📋 Storyboard</strong> on any episode. The AI breaks the script into 3 shots (you can change to 1-6). For each shot:</p>
                <ul className="list-disc list-inside space-y-1 ml-2 mt-1">
                  <li>Click <strong>🪄 Preview</strong> to see a still-image mock of that shot (~$0.001 via Nano Banana — cheap, fast)</li>
                  <li>Don't like it? Edit the prompt and re-preview as many times as you want</li>
                  <li>When all previews look good, <strong>Save Storyboard</strong></li>
                </ul>
              </div>
              <div>
                <p className="font-bold text-zinc-900 mb-1">5. Render the final video</p>
                <p>Click <strong>🎬 Render</strong> on any episode. Veo generates the actual 8-sec clips, stitches them together with your voice narrating the full script. Cost: ~$0.60 per shot (so a 3-shot episode = ~$1.80). Render time: ~3-5 min.</p>
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs">
                <p className="font-bold text-amber-900">⚠️ Cost-saving tip</p>
                <p className="text-amber-800 mt-1">Always preview shots with the 🪄 Preview button (~$0.001) BEFORE clicking 🎬 Render ($0.60 per shot). One bad prompt found cheaply is much better than paying $1.80 for a 3-shot video you don't love.</p>
              </div>
              <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3 text-xs">
                <p className="font-bold text-zinc-900">🎤 About your voice</p>
                <p className="text-zinc-700 mt-1">All voice generation uses your saved default voice (pick it in Script Studio: 9 free OpenAI voices, or your own ElevenLabs cloned voice). Set it once → used everywhere.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Episode script writing modal (side drawer) */}
      <EpisodeScriptModal
        open={scriptModalOpen}
        episode={scriptModalEpisode}
        series={activeSeries}
        onClose={() => setScriptModalOpen(false)}
      />
    </div>
  );
};

export default SeriesStudio;
